import type {
  BillingPortalResponse,
  OrganizationScope,
} from '@/store/features/billing.feature';

export type BillingPortalTrigger = (scope: OrganizationScope) => {
  unwrap: () => Promise<BillingPortalResponse>;
};

/**
 * Resolve a portal destination from the active Clerk organization. Keeping
 * this contract outside the route makes the RTK unwrap and tenant checks
 * independently testable without rendering the entire billing page.
 */
export async function loadBillingPortalDestination(
  organizationId: string,
  trigger: BillingPortalTrigger
): Promise<string> {
  if (!organizationId) {
    throw new Error('No active organization is available.');
  }

  const response = await trigger({ organizationId }).unwrap();
  // Tolerate a MISSING echo (older backend still in prod during rollout) and
  // reject only a present-and-mismatched one, mirroring
  // assertOrganizationResponse — otherwise FE-before-BE deploy order bricks
  // the billing portal for every org.
  if (
    typeof response.organization_id === 'string' &&
    response.organization_id !== organizationId
  ) {
    throw new Error(
      'The billing portal response did not match the active organization.'
    );
  }
  if (!response.url) {
    throw new Error('The billing portal did not return a destination.');
  }

  let destination: URL;
  try {
    destination = new URL(response.url);
  } catch {
    throw new Error('The billing portal returned an invalid destination.');
  }
  if (destination.protocol !== 'https:') {
    throw new Error('The billing portal returned an unsafe destination.');
  }

  return destination.href;
}
